import type { WorkshopAttribution } from "@/types/workshop.type";

/**
 * Who saved a version of a document, in the reader's words (stage 3): the
 * assistant of a thread is "Workshop", a deployed agent goes by its name, a
 * person by theirs ("you" for the viewer). The thread is named only when it is
 * the viewer's own (threads are private; the server withholds the rest).
 */
export type SavedBy = {
  who: string;
  /** Workshop's own thread: a link when it is the viewer's and still there. */
  thread: { id: string; title: string } | null;
  /** Where, when it is not a thread the viewer can open ("their thread"). */
  where: string | null;
};

export function savedByOf(
  attribution: WorkshopAttribution | null | undefined,
  viewerId: string | null,
): SavedBy | null {
  if (!attribution) return null;
  const person =
    attribution.userId && attribution.userId === viewerId
      ? "you"
      : (attribution.userName ?? "a team member");
  if (attribution.actor === "agent") {
    const name = attribution.deployment?.name;
    return { who: name ? `agent “${name}”` : "a removed agent", thread: null, where: null };
  }
  if (attribution.actor === "user") return { who: person, thread: null, where: null };

  const thread = attribution.thread;
  if (!thread) return { who: "Workshop", thread: null, where: null };
  if (thread.state === "own") {
    return {
      who: "Workshop",
      thread: { id: thread.id, title: thread.title?.trim() || "your thread" },
      where: null,
    };
  }
  if (thread.state === "deleted") return { who: "Workshop", thread: null, where: "a deleted thread" };
  return {
    who: "Workshop",
    thread: null,
    where: person === "you" ? "another chat" : `${person}’s chat`,
  };
}

/** Made by Workshop or a deployed agent rather than a person. */
export function isAgentMade(authorType: string | null | undefined): boolean {
  return authorType === "assistant" || authorType === "agent";
}

function day(value: Date): string {
  return value.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function clock(value: Date): string {
  return value.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", hour12: false });
}

/** "Sep 24, 14:05", or "Sep 24 at 14:05" with `at`. Empty for a date that does not read. */
export function formatDayTime(iso: string | null | undefined, joiner: "," | "at" = ","): string {
  if (!iso) return "";
  const value = new Date(iso);
  if (Number.isNaN(value.getTime())) return "";
  return joiner === "at" ? `${day(value)} at ${clock(value)}` : `${day(value)}, ${clock(value)}`;
}

/** "at 14:31" today, "on Sep 24 at 14:31" before. */
export function formatWhenSaved(iso: string | null | undefined): string {
  if (!iso) return "";
  const value = new Date(iso);
  if (Number.isNaN(value.getTime())) return "";
  const today = new Date();
  const sameDay =
    value.getFullYear() === today.getFullYear() &&
    value.getMonth() === today.getMonth() &&
    value.getDate() === today.getDate();
  return sameDay ? `at ${clock(value)}` : `on ${day(value)} at ${clock(value)}`;
}
